import { useState, useEffect } from 'react';

/**
 * Hook to detect media query matches
 * @param query - CSS media query string (e.g., '(min-width: 768px)')
 * @returns boolean indicating if the query matches
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    // SSR-safe: default to false if window is not available
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    setMatches(mediaQuery.matches);

    const handler = (event: MediaQueryListEvent) => setMatches(event.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

/**
 * Convenience hook for detecting desktop breakpoint (>= 768px)
 * @returns boolean indicating if viewport is desktop-sized
 */
export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 768px)');
}

/**
 * Convenience hook for detecting phone breakpoint (< 480px)
 * @returns boolean indicating if viewport is phone-sized
 */
export function useIsPhone(): boolean {
  return useMediaQuery('(max-width: 479px)');
}

/**
 * Convenience hook for detecting tablet breakpoint (480px - 767px)
 * @returns boolean indicating if viewport is tablet-sized
 */
export function useIsTablet(): boolean {
  return useMediaQuery('(min-width: 480px) and (max-width: 767px)');
}

/**
 * Convenience hook for detecting tablet landscape breakpoint (768px - 1199px)
 * @returns boolean indicating if viewport is tablet landscape-sized
 */
export function useIsTabletLandscape(): boolean {
  return useMediaQuery('(min-width: 768px) and (max-width: 1199px)');
}

/**
 * Convenience hook for detecting narrow desktop breakpoint (744px - 1023px).
 * This covers iPad mini 6 portrait (744 CSS px) and similar smaller tablets.
 * At these widths the sidebar takes ~280px leaving limited room for content,
 * so we use a more compact layout.
 * @returns boolean indicating if viewport is narrow-desktop-sized
 */
export function useIsNarrowDesktop(): boolean {
  return useMediaQuery('(min-width: 744px) and (max-width: 1023px)');
}
